<template>
  <div>
    <h3>
      {{ $t('Supervision Management') }}
    </h3>
    <v-row>
      <v-col lg="6">
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="averageTableHeaders"
              :items="supervisionTypesData || []"
              calculate-widths
              show-expand
              item-key="type"
            >
              <template v-slot:top>
                <div class="d-flex">
                  <v-btn
                    class="ml-auto"
                    color="secondary"
                    @click="exportAverageTable"
                  >
                    {{ $t('export') }}
                  </v-btn>
                </div>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td
                  class="pa-2"
                  :colspan="headers.length"
                >
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">
                            {{ $t('Laboratory') }}
                          </th>
                          <th class="text-left">
                            {{ $t('Manager') }}
                          </th>
                          <th class="text-left">
                            {{ $t('Address') }}
                          </th>
                          <th class="text-left">
                            {{ $t('Telephone') }}
                          </th>
                          <th class="text-left">
                            {{ $t('Average score') }}
                          </th>

                          <th class="text-left" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="labSupervision in item.byLab"
                          :key="labSupervision.lab.id + item.type"
                        >
                          <td>{{ labSupervision.lab.name }}</td>

                          <td>
                            <span v-if="labSupervision.lab.manager">
                              {{ labSupervision.lab.manager.firstName }} {{ labSupervision.lab.manager.lastName }}
                            </span>
                          </td>
                          <td>{{ labSupervision.lab.address }}</td>
                          <td>{{ labSupervision.lab.telephone }}</td>
                          <td>{{ labSupervision.avg }}</td>

                          <td>
                            <v-btn
                              icon
                              :to="{name: 'LaboratoryView', params: {laboratoryId: labSupervision.lab.id}}"
                              target="_blank"
                            >
                              <v-icon
                                small
                              >
                                mdi-open-in-new
                              </v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </td>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col lg="6">
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="openRecommendationsTableHeaders"
              :items="openRecommendationsData"
              calculate-widths
            >
              <template v-slot:top>
                <div class="d-flex">
                  <v-btn
                    class="ml-auto"
                    color="secondary"
                    @click="exportOpenRecommendationsTable"
                  >
                    {{ $t('export') }}
                  </v-btn>
                </div>
              </template>

              <template v-slot:item.lab.manager="{ item }">
                <span v-if="item.lab.manager">
                  {{ item.lab.manager.firstName }} {{ item.lab.manager.lastName }}
                </span>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon
                  :to="{name: 'LaboratoryView', params: {laboratoryId: item.lab.id}}"
                  target="_blank"
                >
                  <v-icon
                    small
                  >
                    mdi-open-in-new
                  </v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col lg="6">
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="supervisionDataTableHeaders"
              :items="supervisionData || []"
              calculate-widths
              show-expand
              item-key="years"
            >
              <template v-slot:top>
                <div class="d-flex">
                  <v-btn
                    class="ml-auto"
                    color="secondary"
                    @click="exportSupervisionTable"
                  >
                    {{ $t('export') }}
                  </v-btn>
                </div>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td
                  class="pa-2"
                  :colspan="headers.length"
                >
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">
                            {{ $t('Laboratory') }}
                          </th>
                          <th class="text-left">
                            {{ $t('Manager') }}
                          </th>
                          <th class="text-left">
                            {{ $t('Address') }}
                          </th>
                          <th class="text-left">
                            {{ $t('Telephone') }}
                          </th>

                          <th class="text-left" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="labSupervision in item.labs"
                          :key="labSupervision.id + item.years"
                        >
                          <td>{{ labSupervision.name }}</td>

                          <td>
                            <span v-if="labSupervision.manager">
                              {{ labSupervision.manager.firstName }} {{ labSupervision.manager.lastName }}
                            </span>
                          </td>
                          <td>{{ labSupervision.address }}</td>
                          <td>{{ labSupervision.telephone }}</td>

                          <td>
                            <v-btn
                              icon
                              :to="{name: 'LaboratoryView', params: {laboratoryId: labSupervision.id}}"
                              target="_blank"
                            >
                              <v-icon
                                small
                              >
                                mdi-open-in-new
                              </v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </td>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
    import moment from 'moment'

    import {
        export_json_to_excel_custom
    } from '@/vendor/Export2Excel'

    export default {
        name: "LaboratorySupervisionStatistics",

        props: {
            laboratories: {
                type: Array,
                required: true,
            }
        },

        computed: {

            supervisionData() {
                return this.sortSupervisionData();
            },

            supervisionDataTableHeaders() {
                return [
                    {
                        text: this.$t('Supervised in last (years)'),
                        value: 'years',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: this.$t('Number / % of labs'),
                        value: 'statistic',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: '',
                        value: 'data-table-expand'
                    },
                ]
            },

            supervisionTypesData() {
                return Object.values(this.generateSupervisionTypes())
            },

            averageTableHeaders() {
                return [
                    {
                        text: this.$t('Type'),
                        value: 'type',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: this.$t('Average Score'),
                        value: 'avg',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: '',
                        value: 'data-table-expand'
                    },
                ]
            },

            openRecommendationsData() {
                return this.generateOpenRecommendationsData()
            },

            openRecommendationsTableHeaders() {
                return [
                    {
                        text: this.$t('Name'),
                        value: 'lab.name',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: this.$t('Address'),
                        value: 'lab.address',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: this.$t('Telephone'),
                        value: 'lab.telephone',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: this.$t('Manager'),
                        value: 'lab.manager',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: this.$t('Open Recommendations'),
                        value: 'openRecommendations',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: '',
                        value: 'actions',
                        align: 'end',
                        sortable: false,
                    },
                ]
            },


        },

        methods: {
            sortSupervisionData() {
                let currentDate = moment();
                let oneYear = moment(currentDate.subtract(1, 'years'));
                let twoYear = moment(oneYear.subtract(1, 'years'));
                let treeYear = moment(twoYear.subtract(1, 'years'));

                let oneYearSupervised = {labs: [], percent: ''};
                let twoYearSupervised = {labs: [], percent: ''};
                let treeYearSupervised = {labs: [], percent: ''};
                let neverSupervised = {labs: [], percent: ''};

                for (let lab of this.laboratories) {
                    let supervision = lab.supervisions[0] || null;

                    if (!supervision) {
                        neverSupervised.labs.push(lab);
                        continue;
                    }

                    let lastSupervisedDate = moment(supervision.createdAt);

                    if (lastSupervisedDate.isAfter(oneYear)) {
                        oneYearSupervised.labs.push(lab)
                    } else if (lastSupervisedDate.isAfter(twoYear)) {
                        twoYearSupervised.labs.push(lab)
                    } else if (lastSupervisedDate.isAfter(treeYear)) {
                        treeYearSupervised.labs.push(lab)
                    }
                }

                let labLength = this.laboratories.length;

                oneYearSupervised.percent = ((oneYearSupervised.labs.length * 100) / labLength).toFixed(2);
                twoYearSupervised.percent = ((twoYearSupervised.labs.length * 100) / labLength).toFixed(2);
                treeYearSupervised.percent = ((treeYearSupervised.labs.length * 100) / labLength).toFixed(2);
                neverSupervised.percent = ((neverSupervised.labs.length * 100) / labLength).toFixed(2);

                return [
                    {
                        years: this.$t('1 year'),
                        statistic: `${oneYearSupervised.labs.length} / ${oneYearSupervised.percent} %`,
                        labs: oneYearSupervised.labs
                    },
                    {
                        years: this.$t('2 years'),
                        statistic: `${twoYearSupervised.labs.length} / ${twoYearSupervised.percent} %`,
                        labs: twoYearSupervised.labs
                    },
                    {
                        years: this.$t('3 years'),
                        statistic: `${treeYearSupervised.labs.length} / ${treeYearSupervised.percent} %`,
                        labs: treeYearSupervised.labs
                    },
                    {
                        years: this.$t('never'),
                        statistic: `${neverSupervised.labs.length} / ${neverSupervised.percent} %`,
                        labs: neverSupervised.labs
                    }
                ]
            },

            generateSupervisionTypes() {
                let supervisionTypes = {};

                this.laboratories.forEach(lab => {
                    let labSupervisions = {};

                    lab?.supervisions?.forEach(s => {
                        if (!labSupervisions[s.type]) {
                            labSupervisions[s.type] = []
                        }

                        labSupervisions[s.type].push(s)
                    });

                    for (let [sType, sData] of Object.entries(labSupervisions)) {
                        if (!supervisionTypes[sType]) {
                            supervisionTypes[sType] = {type: sType, byLab: []}
                        }

                        let avg = sData.reduce((a, b) => {
                            return a + (parseInt(b.score) || 0)
                        }, 0) / sData.length;

                        supervisionTypes[sType].byLab.push({lab, data: sData, avg})
                    }

                });

                for (let t in supervisionTypes) {
                    supervisionTypes[t].avg = Math.round(supervisionTypes[t].byLab.reduce((a, b) => {
                        return a + (parseInt(b.avg) || 0)
                    }, 0) / supervisionTypes[t].byLab.length);
                }

                return supervisionTypes
            },

            generateOpenRecommendationsData() {
                return this.laboratories.map(lab => {
                    let openRecommendations = 0;
                    lab.supervisions.forEach(s => {
                        openRecommendations += s.recommendations.filter(r => !r.closedDate).length
                    });
                    return {lab, openRecommendations}
                }).filter(e => e.openRecommendations)
            },

            exportAverageTable() {
                let multiData = [];

                {
                    const tHeader = [
                        'Type', 'Average score'
                    ];


                    const filterVal = [
                        'type', 'avg'
                    ];


                    const data = this.supervisionTypesData.map(v => {
                        let filteredData = filterVal.map(j => {
                            if (v[j]) {
                                return v[j].toString()
                            }
                            return ''
                        });

                        return filteredData;
                    }).filter(e => e);

                    multiData.push({name: 'Supervision Average', header: tHeader, data})
                }

                {
                    const tHeader = [
                        '#', 'Id', 'Name', 'Address', 'Manager', 'Telephone', 'Average score'
                    ];


                    const filterVal = [
                        '#', 'id', 'name', 'address', 'manager', 'telephone', 'avg'
                    ];

                    const deepValues = {
                        manager(item) {
                            return item?.manager?.firstName + '' + item?.manager?.lastName || ''
                        }
                    };

                    for (let s of this.supervisionTypesData) {
                        const data = s.byLab.map((v, index) => {
                            return filterVal.map(j => {
                                if (j === '#') return index + 1;
                                if (j === 'avg') return v.avg;
                                if (v.lab[j]) {
                                    if (j in deepValues) {
                                        return deepValues[j](v.lab)
                                    } else {
                                        return v.lab[j].toString()
                                    }
                                }

                                return ''
                            });
                        }).filter(e => e);

                        multiData.push({name: s.type, header: tHeader, data})

                    }
                }

                export_json_to_excel_custom({
                    multiData,
                    filename: 'supervision-average-scoring',
                    autoWidth: true,
                })

            },

            exportOpenRecommendationsTable() {
                let multiData = [];

                {
                    const tHeader = [
                        '#', 'Id', 'Name', 'Address', 'Manager', 'Telephone', 'Open Recommendations'
                    ];


                    const filterVal = [
                        '#', 'id', 'name', 'address', 'manager', 'telephone', 'openRecommendations'
                    ];


                    const deepValues = {
                        manager(item) {
                            return item?.manager?.firstName + '' + item?.manager?.lastName || ''
                        }
                    };

                    const data = this.openRecommendationsData.map((v, index) => {
                        return filterVal.map(j => {
                            if (j === '#') return index + 1;
                            if (j === 'openRecommendations') return v.openRecommendations;
                            if (v.lab[j]) {
                                if (j in deepValues) {
                                    return deepValues[j](v.lab)
                                } else {
                                    return v.lab[j].toString()
                                }
                            }

                            return ''
                        });
                    }).filter(e => e);

                    multiData.push({name: 'Open Recommendations', header: tHeader, data})
                }

                export_json_to_excel_custom({
                    multiData,
                    filename: 'supervision-open-recommendations',
                    autoWidth: true,
                })

            },

            exportSupervisionTable() {
                let multiData = [];

                {
                    const tHeader = [
                        'Supervised in last (years)', 'Number / % of labs'
                    ];


                    const filterVal = [
                        'years', 'statistic'
                    ];


                    const data = this.supervisionData.map(v => {
                        let filteredData = filterVal.map(j => {
                            if (v[j]) {
                                return v[j].toString()
                            }
                            return ''
                        });

                        return filteredData;
                    }).filter(e => e);

                    multiData.push({name: 'Statistic', header: tHeader, data})
                }

                {
                    const tHeader = [
                        '#', 'Id', 'Name', 'Address', 'Manager', 'Telephone'
                    ];


                    const filterVal = [
                        '#', 'id', 'name', 'address', 'manager', 'telephone'
                    ];

                    const deepValues = {
                        manager(item) {
                            return item?.manager?.firstName + '' + item?.manager?.lastName || ''
                        }
                    };

                    for (let s of this.supervisionData) {
                        const data = s.labs.map((v, index) => {
                            return filterVal.map(j => {
                                if (j === '#') return index + 1;
                                if (v[j]) {
                                    if (j in deepValues) {
                                        return deepValues[j](v)
                                    } else {
                                        return v[j].toString()
                                    }
                                }

                                return ''
                            });
                        }).filter(e => e);

                        multiData.push({name: s.years, header: tHeader, data})

                    }
                }

                export_json_to_excel_custom({
                    multiData,
                    filename: 'supervision-statistic',
                    autoWidth: true,
                })

            },


        }

    }
</script>

<style scoped>

</style>
